import { useEffect, useRef, useState, FormEvent } from 'react';

const CONTACT_EMAIL = 'hello@broadmind.cn';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.currentTarget);
    const name = (formData.get('name') || '').toString();
    const company = (formData.get('company') || '').toString();
    const email = (formData.get('email') || '').toString();
    const phone = (formData.get('phone') || '').toString();
    const challenge = (formData.get('challenge') || '').toString();
    const subject = encodeURIComponent(`咨询：${company || name || '预约交流'}`);
    const body = encodeURIComponent(
      `姓名：${name}\n公司：${company}\n邮箱：${email}\n手机：${phone}\n\n面临的主要挑战：\n${challenge}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <section id="contact-section" ref={sectionRef} className="py-24 md:py-32 bg-[#0F172A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div
            className="transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <span className="text-xs uppercase tracking-widest text-white/40 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Get Started
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold text-white leading-tight mb-8"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              如果你也意识到了变化，
              <br />
              <span className="text-[#F97316]">现在就是开始的时候。</span>
            </h2>
            <p
              className="text-base text-white/50 leading-relaxed mb-10"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              预约一次 30 分钟的免费交流，我们会认真了解你的情况，给出真实的判断，而不是销售话术。
            </p>

            <div className="flex flex-col gap-5">
              {[
                { icon: 'ri-time-line', text: '30 分钟免费交流' },
                { icon: 'ri-shield-line', text: '不推销，只判断是否合适' },
                { icon: 'ri-map-pin-line', text: '线上 / 线下均可' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5">
                    <i className={`${item.icon} text-[#F97316] text-sm`} />
                  </div>
                  <span className="text-sm text-white/60" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div
            className="transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '200ms' }}
          >
            {submitted ? (
              <div className="bg-white/5 rounded-2xl p-10 border border-white/10 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F97316]/20 mx-auto mb-6">
                  <i className="ri-check-line text-[#F97316] text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                  已为你打开邮件客户端
                </h3>
                <p className="text-sm text-white/50" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                  请发送邮件至 <a className="underline text-[#F97316]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>，我们会在 1 个工作日内回复。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs text-white/40 mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                      姓名 <span className="text-[#F97316]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="你的姓名"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-colors"
                      style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                      公司 <span className="text-[#F97316]">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      placeholder="公司名称"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-colors"
                      style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    邮箱 <span className="text-[#F97316]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    手机
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="你的联系电话"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-colors"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 mb-2" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                    你目前面临的主要挑战
                  </label>
                  <textarea
                    name="challenge"
                    rows={3}
                    maxLength={500}
                    placeholder="简单描述你的情况，比如：团队规模、当前痛点、期望目标..."
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-white/30 transition-colors resize-none"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors disabled:opacity-60"
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {submitting ? '提交中...' : '预约交流'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
